import { ApiError } from '@l2u/web-shared';

// Which workspace is asking for a sign-in link?
//
// The API has three ways to answer that (apps/api/src/routes/auth.ts): the
// request hostname, the browser's Origin, and a caller-supplied `tenant_slug`.
// The first two are free — a user on `<slug>.<TENANT_BASE_DOMAIN>` is resolved
// with no input at all. On the SHARED platform host neither fires, and the
// portal has to supply the slug or the endpoint cannot know which workspace to
// search.
//
// So the field is revealed on demand rather than always shown: submit with
// whatever we have, and only ask if the API says it could not tell. That way a
// tenant subdomain never shows a field the user does not need, and the shared
// host degrades to one extra question instead of a dead end.

/** Only this code means "supply a workspace"; everything else is a real error. */
export function needsWorkspace(err: unknown): boolean {
  return err instanceof ApiError && err.code === 'workspace_required';
}

// Users paste whatever they were given — a bare slug, the hostname, or the full
// URL. For a `<slug>.<base>` host the first label IS the slug, which is the case
// this has to get right. A tenant on a CUSTOM domain would be mangled
// (`compliance.acme.com` yields `compliance`), but a custom domain resolves by
// hostname on the server and never reaches this field.
export function normaliseWorkspace(input: string): string {
  let value = input.trim().toLowerCase();
  if (!value) return '';
  // Strip a scheme and anything from the first slash, so a pasted URL works.
  value = value.replace(/^[a-z]+:\/\//, '');
  value = value.split('/')[0]!;
  return value.split('.')[0] ?? '';
}

export type LoginLinkBody = { email: string; tenant_slug?: string };

/**
 * `urlSlug` is the `:tenantSlug` route param (an emailed deep link); `typed` is
 * whatever is in the revealed field. Neither present sends no slug at all,
 * leaving the API's hostname/Origin strategies to answer.
 *
 * TYPED WINS when it is non-empty, which reads backwards until you follow the
 * sequence: the field is only ever revealed because the API rejected what we
 * already sent. On the first submit `typed` is empty and the URL slug is used.
 * If that slug is stale — the workspace was renamed, or the link is old — the
 * user is shown the field precisely so they can correct it, and preferring the
 * URL there would resend the rejected slug forever and strand them on a filled
 * form that changes nothing.
 */
export function loginLinkBody(
  email: string,
  urlSlug: string | undefined,
  typed: string,
): LoginLinkBody {
  // BOTH sides go through normaliseWorkspace: the route param is as likely to
  // arrive mixed-case as anything typed, and control.tenants.slug is matched
  // case-sensitively, so `/c/login/ACME` would otherwise miss a tenant that
  // exists.
  const slug = normaliseWorkspace(typed) || normaliseWorkspace(urlSlug ?? '');
  const body: LoginLinkBody = { email: email.trim() };
  if (slug) body.tenant_slug = slug;
  return body;
}
